import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  collection,
  addDoc,
  increment,
  runTransaction,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';
import type { UserEngagementMetrics, MetricType } from '@/types/donation';
import { DEFAULT_ENGAGEMENT_METRICS } from '@/types/donation'; // eslint-disable-line no-duplicate-imports
import type { PrayerType } from '@/types/prayers';

// ============================================
// PATH HELPERS
// ============================================

function getEngagementMetricsPath(userId: string): string {
  return `artifacts/${appId}/users/${userId}/metrics/engagement`;
}

// ============================================
// DATE UTILITIES
// ============================================

/**
 * Get today's date in YYYY-MM-DD format.
 */
function getTodayDateString(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get the Monday of the current week in YYYY-MM-DD format.
 */
function getWeekStartDateString(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  const monday = new Date(now.setDate(diff));
  return monday.toISOString().split('T')[0];
}

/**
 * Check if we need to reset daily metrics.
 */
function needsDayReset(metrics: UserEngagementMetrics): boolean {
  const today = getTodayDateString();
  return metrics.dayResetDate !== today;
}

/**
 * Check if we need to reset weekly metrics.
 */
function needsWeekReset(metrics: UserEngagementMetrics): boolean {
  const weekStart = getWeekStartDateString();
  return metrics.weekResetDate !== weekStart;
}

/**
 * Reset daily metrics and return updated metrics.
 */
function resetDailyMetrics(metrics: UserEngagementMetrics): Partial<UserEngagementMetrics> {
  const today = getTodayDateString();

  // Check if this is a consecutive day
  let consecutiveActiveDays = metrics.consecutiveActiveDays;
  if (metrics.lastActiveDate) {
    const lastDate = new Date(metrics.lastActiveDate);
    const todayDate = new Date(today);
    const diffDays = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      consecutiveActiveDays += 1;
    } else if (diffDays > 1) {
      consecutiveActiveDays = 1; // Reset streak
    }
  } else {
    consecutiveActiveDays = 1;
  }

  return {
    messagesThisDay: 0,
    reactionsGivenThisDay: 0,
    reactionsReceivedThisDay: 0,
    sessionMinutesToday: 0,
    currentSessionStart: null,
    dayResetDate: today,
    lastActiveDate: today,
    consecutiveActiveDays,
    // Reading daily metrics
    readingTimeThisDay: 0,
    versesReadThisDay: 0,
    // Prayer hold daily metrics
    prayerHoldsThisDay: 0,
    prayerHoldTimeThisDay: 0,
  };
}

/**
 * Reset weekly metrics and return updated metrics.
 */
function resetWeeklyMetrics(): Partial<UserEngagementMetrics> {
  return {
    messagesThisWeek: 0,
    checkInsThisWeek: 0,
    weekResetDate: getWeekStartDateString(),
    // Reading weekly metrics
    readingTimeThisWeek: 0,
    versesReadThisWeek: 0,
    resourcesViewedThisWeek: 0,
    // Prayer hold weekly metrics
    prayerHoldsThisWeek: 0,
    prayerHoldTimeThisWeek: 0,
  };
}

// ============================================
// CORE FUNCTIONS
// ============================================

/**
 * Get or initialize user's engagement metrics.
 * Handles day/week resets automatically.
 * Uses a transaction to prevent concurrent requests from both applying resets,
 * which could zero out metrics that were just incremented.
 */
export async function getUserEngagementMetrics(userId: string): Promise<UserEngagementMetrics> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));

  return runTransaction(db, async (transaction) => {
    const metricsSnap = await transaction.get(metricsRef);

    if (!metricsSnap.exists()) {
      // Initialize with defaults
      const today = getTodayDateString();
      const weekStart = getWeekStartDateString();
      const initialMetrics: UserEngagementMetrics = {
        ...DEFAULT_ENGAGEMENT_METRICS,
        dayResetDate: today,
        weekResetDate: weekStart,
        lastActiveDate: today,
        consecutiveActiveDays: 1,
      };

      transaction.set(metricsRef, {
        ...initialMetrics,
        updatedAt: serverTimestamp(),
      });

      return initialMetrics;
    }

    const metrics = metricsSnap.data() as UserEngagementMetrics;
    const updates: Partial<UserEngagementMetrics> = {};

    // Check for day reset
    if (needsDayReset(metrics)) {
      Object.assign(updates, resetDailyMetrics(metrics));
    }

    // Check for week reset
    if (needsWeekReset(metrics)) {
      Object.assign(updates, resetWeeklyMetrics());
    }

    // Apply updates if needed
    if (Object.keys(updates).length > 0) {
      transaction.update(metricsRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { ...metrics, ...updates };
    }

    return metrics;
  });
}

/**
 * Increment a metric by amount (default 1).
 * Uses Firestore's atomic increment() to prevent race conditions
 * where concurrent increments could lose updates.
 *
 * Note: Day/week resets are handled separately by getUserEngagementMetrics()
 * which is called on read. This ensures resets happen on first access each day/week.
 */
export async function incrementMetric(
  userId: string,
  metric: MetricType,
  amount: number = 1
): Promise<void> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));

  // Map metric types to document fields
  const metricFieldMap: Record<string, keyof UserEngagementMetrics> = {
    messagesThisDay: 'messagesThisDay',
    messagesThisWeek: 'messagesThisWeek',
    reactionsGivenThisDay: 'reactionsGivenThisDay',
    reactionsReceivedThisDay: 'reactionsReceivedThisDay',
    checkInsThisWeek: 'checkInsThisWeek',
    prayerDonationClicksThisWeek: 'prayerDonationClicksThisWeek',
    // Reading metrics
    readingTimeThisDay: 'readingTimeThisDay',
    readingTimeThisWeek: 'readingTimeThisWeek',
    versesReadThisDay: 'versesReadThisDay',
    versesReadThisWeek: 'versesReadThisWeek',
    resourcesViewedThisWeek: 'resourcesViewedThisWeek',
    parshasCompletedTotal: 'parshasCompletedTotal',
    currentReadingStreak: 'currentReadingStreak',
    // Prayer hold metrics
    prayerHoldsThisDay: 'prayerHoldsThisDay',
    prayerHoldsThisWeek: 'prayerHoldsThisWeek',
    prayerHoldTimeThisDay: 'prayerHoldTimeThisDay',
    prayerHoldTimeThisWeek: 'prayerHoldTimeThisWeek',
    prayerHoldsTotal: 'prayerHoldsTotal',
    prayerHoldTimeTotal: 'prayerHoldTimeTotal',
    healingPrayerHolds: 'healingPrayerHolds',
    memoryPrayerHolds: 'memoryPrayerHolds',
    dedicationPrayerHolds: 'dedicationPrayerHolds',
  };

  const field = metricFieldMap[metric];
  if (!field) {
    console.warn(`Metric ${metric} cannot be incremented directly`);
    return;
  }

  // Ensure the document exists first (upsert pattern)
  // This handles the case where the user has never had metrics before
  const metricsSnap = await getDoc(metricsRef);
  if (!metricsSnap.exists()) {
    // Initialize with defaults if document doesn't exist
    await getUserEngagementMetrics(userId);
  }

  // Use atomic increment - no read required, prevents race conditions
  // Concurrent increments will all be applied correctly
  await updateDoc(metricsRef, {
    [field]: increment(amount),
    lastActiveDate: getTodayDateString(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update session activity.
 * Call periodically while app is active to track session time.
 */
export async function updateSessionActivity(userId: string): Promise<void> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));
  const metrics = await getUserEngagementMetrics(userId);

  const now = Timestamp.now();

  if (metrics.currentSessionStart) {
    // Calculate minutes since session start
    const sessionStartMs = metrics.currentSessionStart.toMillis();
    const nowMs = now.toMillis();
    const sessionMinutes = Math.floor((nowMs - sessionStartMs) / (1000 * 60));

    await updateDoc(metricsRef, {
      sessionMinutesToday: sessionMinutes,
      lastActiveDate: getTodayDateString(),
      updatedAt: serverTimestamp(),
    });
  } else {
    // Start new session
    await updateDoc(metricsRef, {
      currentSessionStart: now,
      lastActiveDate: getTodayDateString(),
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Start a new session. Call when app becomes active.
 */
export async function startSession(userId: string): Promise<void> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));

  // Ensure metrics exist
  await getUserEngagementMetrics(userId);

  await updateDoc(metricsRef, {
    currentSessionStart: Timestamp.now(),
    lastActiveDate: getTodayDateString(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * End the current session. Call when app becomes inactive.
 */
export async function endSession(userId: string): Promise<void> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));
  const metrics = await getUserEngagementMetrics(userId);

  if (metrics.currentSessionStart) {
    const sessionStartMs = metrics.currentSessionStart.toMillis();
    const nowMs = Timestamp.now().toMillis();
    const additionalMinutes = Math.floor((nowMs - sessionStartMs) / (1000 * 60));

    await updateDoc(metricsRef, {
      sessionMinutesToday: (metrics.sessionMinutesToday || 0) + additionalMinutes,
      currentSessionStart: null,
      updatedAt: serverTimestamp(),
    });
  }
}

/**
 * Subscribe to engagement metrics changes (real-time).
 * Returns unsubscribe function.
 */
export function subscribeToEngagementMetrics(
  userId: string,
  callback: (metrics: UserEngagementMetrics | null) => void
): () => void {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));

  return onSnapshot(
    metricsRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as UserEngagementMetrics);
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error subscribing to engagement metrics:', error);
      callback(null);
    }
  );
}

/**
 * Increment reactions received for a user.
 * Called when someone reacts to this user's message.
 * Uses atomic increment to prevent race conditions.
 */
export async function incrementReactionsReceived(
  userId: string,
  amount: number = 1
): Promise<void> {
  const metricsRef = doc(db, getEngagementMetricsPath(userId));

  // Ensure metrics document exists
  const metricsSnap = await getDoc(metricsRef);
  if (!metricsSnap.exists()) {
    await getUserEngagementMetrics(userId);
  }

  // Use atomic increment - prevents race conditions
  await updateDoc(metricsRef, {
    reactionsReceivedThisDay: increment(amount),
    updatedAt: serverTimestamp(),
  });
}

// ============================================
// PRAYER DONATION CLICK TRACKING
// ============================================

/**
 * Path for prayer donation click analytics.
 */
function getPrayerDonationClicksPath(): string {
  return `artifacts/${appId}/prayer_donation_clicks`;
}

/**
 * Track a user clicking a donation button on a prayer.
 * This performs dual tracking:
 * 1. Increments a counter in user engagement metrics
 * 2. Logs a detailed record for analytics
 */
export async function trackPrayerDonationClick(
  userId: string,
  prayerId: string,
  templateId: string
): Promise<void> {
  // 1. Increment counter in user engagement metrics
  await incrementMetric(userId, 'prayerDonationClicksThisWeek');

  // 2. Add detailed click record for analytics
  const clicksRef = collection(db, getPrayerDonationClicksPath());
  await addDoc(clicksRef, {
    userId,
    prayerId,
    templateId,
    clickedAt: serverTimestamp(),
  });
}

// ============================================
// PRAYER HOLD TRACKING
// ============================================

const PRAYER_TYPE_METRIC_MAP: Record<PrayerType, MetricType> = {
  healing: 'healingPrayerHolds',
  memory: 'memoryPrayerHolds',
  dedication: 'dedicationPrayerHolds',
};

/**
 * Track a completed prayer hold meditation.
 * Performs triple tracking:
 * 1. Increments rolling counters in user engagement metrics (atomic)
 * 2. Conditionally updates the personal-record longest hold
 * 3. Logs a detailed record to the prayer_holds subcollection
 */
export async function trackPrayerHoldComplete(
  userId: string,
  prayerId: string,
  prayerName: string,
  prayerType: PrayerType,
  durationMs: number,
  isPinned: boolean
): Promise<void> {
  const durationSec = Math.round(durationMs / 1000);

  // 1. Increment rolling counters (all atomic, fire in parallel)
  await Promise.all([
    incrementMetric(userId, 'prayerHoldsThisDay'),
    incrementMetric(userId, 'prayerHoldsThisWeek'),
    incrementMetric(userId, 'prayerHoldTimeThisDay', durationSec),
    incrementMetric(userId, 'prayerHoldTimeThisWeek', durationSec),
    incrementMetric(userId, 'prayerHoldsTotal'),
    incrementMetric(userId, 'prayerHoldTimeTotal', durationSec),
    incrementMetric(userId, PRAYER_TYPE_METRIC_MAP[prayerType]),
  ]);

  // 2. Conditional update for longestPrayerHoldSec (can't use increment)
  try {
    const metricsRef = doc(db, getEngagementMetricsPath(userId));
    const metricsSnap = await getDoc(metricsRef);
    if (metricsSnap.exists()) {
      const current = (metricsSnap.data() as UserEngagementMetrics).longestPrayerHoldSec || 0;
      if (durationSec > current) {
        await updateDoc(metricsRef, { longestPrayerHoldSec: durationSec });
      }
    }
  } catch (error) {
    console.error('Error updating longestPrayerHoldSec:', error);
  }

  // 3. Log individual prayer hold record
  try {
    const holdsRef = collection(db, `artifacts/${appId}/users/${userId}/prayer_holds`);
    await addDoc(holdsRef, {
      prayerId,
      prayerName,
      prayerType,
      durationMs,
      isPinned,
      heldAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error logging prayer hold record:', error);
  }
}
